import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence, Variants } from 'framer-motion';

import { GlassesPackageDisplay } from '../GlassesPackageDisplay';
import { IGlassesPackage, IFeature, IAddOn } from '../../types';
import { AddOnSelector } from '../AddOnSelector';
import { Sticker } from '../Sticker';
import { PricingOverview } from '../PricingOverview';
import { breakpoints, colors } from '../../constants';

const animationVariants: Variants = {
    enter: (slideLeft: boolean) => ({
        x: slideLeft ? '-100%' : '100%',
        position: 'absolute',
    }),
    center: { x: 0, position: 'static' },
    exit: (slideLeft: boolean) => ({
        x: slideLeft ? '100%' : '-100%',
        position: 'absolute',
    }),
};

const PricingOverviewContainer = styled.div``;

interface IProps {
    className?: string;
    addOns: IAddOn[];
    features: IFeature[];
    selectedPackage: IGlassesPackage;
    selectedAddOns: IAddOn[];
    onAddOnClick(addOn: IAddOn): void;
}

const BaseGlassesPackageView = ({
    className,
    addOns,
    features,
    selectedPackage,
    selectedAddOns,
    onAddOnClick,
}: IProps) => {
    const previousPackageRef = useRef(selectedPackage);

    const getSlideDirection = (): 'left' | 'right' => {
        if (
            selectedPackage.multifocal !== previousPackageRef.current.multifocal
        ) {
            return selectedPackage.multifocal ? 'right' : 'left';
        }

        return selectedPackage.order > previousPackageRef.current.order
            ? 'right'
            : 'left';
    };

    useEffect(() => {
        previousPackageRef.current = selectedPackage;
    }, [selectedPackage]);

    const slideLeft = getSlideDirection() === 'left';

    const showSticker =
        selectedPackage.topChoice ||
        (selectedPackage.name === 'Excellent' && !selectedPackage.multifocal);

    return (
        <div className={className}>
            <AnimatePresence custom={slideLeft}>
                <motion.div
                    animate="center"
                    custom={slideLeft}
                    exit="exit"
                    initial="enter"
                    key={selectedPackage.id}
                    transition={{ ease: 'easeInOut' }}
                    variants={animationVariants}
                >
                    <PricingOverviewContainer>
                        {showSticker && (
                            <Sticker topChoice={selectedPackage.topChoice}>
                                {selectedPackage.topChoice
                                    ? 'Meest gekozen'
                                    : 'Alleen in de winkels'}
                            </Sticker>
                        )}
                        <PricingOverview
                            selectedAddOns={selectedAddOns}
                            selectedPackage={selectedPackage}
                        />
                        {!!addOns.length && (
                            <AddOnSelector
                                addOns={addOns}
                                discountPercentage={
                                    selectedPackage.discountPercentage
                                }
                                selectedAddOns={selectedAddOns}
                                onAddOnClick={onAddOnClick}
                            />
                        )}
                    </PricingOverviewContainer>
                    <GlassesPackageDisplay
                        features={features}
                        selectedPackage={selectedPackage}
                    />
                </motion.div>
            </AnimatePresence>
        </div>
    );
};

export const GlassesPackageView = styled(BaseGlassesPackageView)`
    position: relative;
    overflow: hidden;

    ${PricingOverviewContainer} {
        display: flex;
        flex-direction: column;
        position: relative;
        background-color: ${colors.white};
        padding-top: ${({ selectedPackage }) =>
            selectedPackage.topChoice && '15px'};
    }

    ${Sticker} {
        position: absolute;
        top: 0;
        right: 5%;
    }

    ${AddOnSelector} {
        margin-top: auto;
    }

    > div {
        width: 100%;
        padding: 20px;

        @media (${breakpoints.mediumMin}) {
            display: flex;
            min-height: auto;
            padding: 40px 80px;

            ${PricingOverviewContainer} {
                width: 55%;
                margin: 10px 40px 10px 10px;
                box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
                padding-top: 0;
            }
        }
    }
`;
